import { Droppable, DragDropContext, Draggable } from "react-beautiful-dnd";
import { t } from "i18next";
import styled from "styled-components";
import { ActionGroup } from "..";
import { useRootStore } from "../../../stores/hooks";
import { StyledButtonContainer } from "./style";
import { SmallAddActionButton } from "../../SmallAddActionButton";
import { useDraggableInPortal } from "../../../hooks/use-draggable-in-portal";
import { AddedEventButton } from "../../AddedEventButton";
import { v4 as uuid } from "uuid";
import * as A from "../../../stores/automation/mergers/actions";
import { observer } from "mobx-react-lite";
import type { ActionGroupType } from "../../../types/candidate";
import type { CurrSection } from "../../../containers/AutomationForm/types";
import { triggersIdsAvailableForMergeRecordsAction } from "../../../stores/automation/utils/inputValidation";
import { useActionDragDrop } from "../../../hooks/use-action-drag-drop";
import {
  activateNextSection,
  handleActionChange,
  showActionFieldMissingError,
  showActionFieldsEmptyError,
  showActionError,
} from "../../../utils/action-helpers";
import type { ActionReference } from "@smartsuite/automation-engine-api";
import type { CandidateStore } from "../../../stores/automation/candidate.store";
import { BranchesBlockCard } from "../Branches/BranchesBlockCard";
import { NestedBranchesFan } from "../Branches/NestedBranchesFan";

/* Nested Advanced Logic (Task 7b): a nested Conditional Logic block's own
   fan is wider than the root Looping container's original 420px-cap
   geometry -- same rationale as NestedLoopGroup's ColumnActionGroup
   override. Applied ONLY when the root loop holds an advanced group, so a
   flat root loop keeps ActionGroup's own list-mode rendering untouched. */
const WideActionGroup = styled(ActionGroup)`
  max-width: none;
  width: max-content;
  min-width: 420px;
  /* Only applied when the loop hosts a nested branches block, which makes the
     container grow to the fan's width. The nested block card + fan self-center
     over that width (margin auto / 50% connector stub); center the loop's own
     action cards and Add Action too so the whole spine lines up (matches the
     BranchColumn reference). The header stays put -- it's width:100%. */
  align-items: center;
`;

interface LoopingProps {
  isActive: boolean;
  activeAction: string | undefined;
  setActiveAction: (hash?: string) => void;
  setActiveTrigger: (isActive?: boolean) => void;
  setActiveSection: () => void;
  onClickAction: (hash?: string) => void;
  onClickTrigger: () => void;
  onChangeAction: (
    integrationId: string,
    actionId: string,
    actionGroupType?: ActionGroupType,
  ) => void;
  /** Nested Advanced Logic (Task 7b): the currently active canvas section --
   * used to compute selection state for the nested block/fan the root loop
   * may itself contain. */
  activeItem: CurrSection;
  /** Nested Advanced Logic (Task 7b): selects a branch of the nested block's
   * own fan -- forwarded straight into NestedBranchesFan's `onSelect`. */
  onSelectBranch: (instanceId: number, branchHash: string) => void;
  /** Nested Advanced Logic (Task 7b): selects a newly-added (or clicked)
   * nested Conditional Logic block living inside the root loop. */
  onSelectNestedBlock: (instanceId: number) => void;
  /** Nested Advanced Logic (Task 7b): selects a nested loop living inside
   * one of the root loop's own branches -- forwarded into NestedBranchesFan. */
  onSelectNestedLoop: (instanceId: number) => void;
}

export const getGroupName = (candidate: CandidateStore): string => {
  if (candidate.looping.loopName) return candidate.looping.loopName;
  const groupName = candidate.looping.getDefaultGroupName();
  return groupName.isSourceSet
    ? groupName.groupName
    : t("general.repeat-for-each-in");
};

export const Looping = observer(
  ({
    isActive,
    activeAction,
    setActiveSection,
    setActiveAction,
    setActiveTrigger,
    onClickAction,
    onClickTrigger,
    onChangeAction,
    activeItem,
    onSelectBranch,
    onSelectNestedBlock,
    onSelectNestedLoop,
  }: LoopingProps): React.ReactElement | null => {
    const store = useRootStore();
    const renderDraggable = useDraggableInPortal();

    const candidate = store.automations.candidate;
    const actionGroup = candidate.self.actionGroups.find(
      (actionGroup) => actionGroup.config.oneofKind === "loop",
    );

    const actions = candidate.groupActions;
    const loopingActions = candidate.looping.loopActions;

    const { handleDragStart, handleDragEnd } = useActionDragDrop({
      actions: candidate.looping.loopActions,
      onClickAction,
      setActions: (loopActions) => candidate.looping.setActions(...loopActions),
      setActionsWithOutReferences: (
        reference?: string,
        whatActionReferencesRemove?: ActionReference,
      ) =>
        candidate.looping.setActionsWithOutReferences(
          reference,
          whatActionReferencesRemove,
        ),
    });

    const handleDelete = (
      e: React.MouseEvent<HTMLDivElement>,
      currActionHash: string,
    ): void => {
      e.stopPropagation();
      candidate.looping.setActions(
        A.ejectActionReferences(currActionHash),
        A.ejectAction(currActionHash),
      );
      activateNextSection(
        currActionHash,
        loopingActions,
        setActiveSection,
        onClickAction,
      );
    };

    const handleClickAction = (accessError?: boolean, hash?: string): void => {
      if (!accessError) onClickAction(hash);
    };

    const handleDuplicate = (hash: string): void => {
      candidate.looping.setActions(A.mergeDuplicateAction(hash));
    };

    const handleDeleteLooping = (): void => {
      if (!actionGroup) return;
      const rootLoopId = actionGroup.instanceId;

      const performDelete = (): void => {
        if (!candidate.branches.deleteNestedLoop(rootLoopId)) return;
        const nextActionHash = actions[actions.length - 1]?.hash ?? "";
        nextActionHash ? onClickAction(nextActionHash) : onClickTrigger();
      };

      if (!candidate.branches.loopHasContent(rootLoopId)) {
        performDelete();
        return;
      }

      store.modal.openConfirmModal({
        type: "warning",
        title: t("confirm-modal.are-you-sure"),
        description: `${t("branches.delete-loop-confirm")}. ${t(
          "confirm-modal.cannot-undone-action",
        )}.`,
        positiveButtonText: t("branches.yes-delete-loop"),
        negativeButtonText: t("confirm-modal.no-cancel"),
        onClose: () => store.modal.closeConfirmModal(),
        onConfirm: () => {
          performDelete();
          store.modal.closeConfirmModal();
        },
      });
    };

    const showLoopingActionErrors = (
      action: (typeof loopingActions)[number],
    ): boolean => {
      const isSubmitted = candidate.isActionSubmitted(action.hash);

      if (!isSubmitted && candidate.isNewlyAddedAction(action)) {
        return false;
      }

      return (
        showActionFieldsEmptyError(action, isSubmitted) ||
        showActionFieldMissingError(
          store.automations.isAllDynamicDescriptionsLoaded(),
          candidate.self,
          action,
        )
      );
    };

    // The loop's source resolves against the trigger/action output
    // descriptions, which are fetched asynchronously when an automation is
    // opened. Until they arrive the source reads as "not set", so don't flag
    // the loop as empty/invalid yet -- that is a false warning (BUG-21925).
    const isDescriptionsLoaded =
      store.automations.isAllDynamicDescriptionsLoaded();
    const isSourceSet =
      !isDescriptionsLoaded ||
      candidate.looping.getDefaultGroupName().isSourceSet;
    const isSourceError =
      isDescriptionsLoaded && !candidate.looping.isConfiguredSourceValid;
    const groupName = getGroupName(candidate);

    const handleLoopingClick = (): void => {
      setActiveAction(undefined);
      setActiveTrigger(undefined);
      setActiveSection();
    };

    if (!actionGroup) return null;

    const rootLoopId = actionGroup.instanceId;
    const advanced = candidate.branches.loopAdvancedGroup(rootLoopId);
    const Container = advanced !== undefined ? WideActionGroup : ActionGroup;

    return (
      <Container
        isActive={isActive}
        isEmpty={!isSourceSet}
        isError={isSourceError}
        title={t("general.looping")}
        name={groupName}
        setIsActive={handleLoopingClick}
        onDelete={handleDeleteLooping}
      >
        {/* Filled action button */}
        <StyledButtonContainer onClick={handleLoopingClick}>
          <DragDropContext
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <Droppable
              droppableId="action-droppable"
              isDropDisabled={loopingActions.some((a) => a.deletedError)}
            >
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={{ position: "relative" }}
                >
                  {loopingActions.map((action, index) => (
                    <div key={action.hash}>
                      <Draggable
                        key={action.hash}
                        draggableId={action.hash}
                        index={index}
                      >
                        {renderDraggable((provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <AddedEventButton
                              groupType="loop"
                              eventType="action"
                              key={index}
                              isHighlighted={activeAction === action.hash}
                              isActive={activeAction === action.hash}
                              action={action}
                              error={showActionError(
                                action,
                                candidate.actionsSubmitted,
                                triggersIdsAvailableForMergeRecordsAction,
                              )}
                              fieldMissingError={showLoopingActionErrors(
                                action,
                              )}
                              disabled={
                                (action.accessError ?? false) ||
                                (action.integrationDisabled ?? false) ||
                                (action.actionDisabled ?? false)
                              }
                              onClick={() => {
                                handleClickAction(
                                  action.accessError,
                                  action.hash,
                                );
                              }}
                              onDuplicate={handleDuplicate}
                              onDelete={(e, currActionHash) => {
                                handleDelete(
                                  e as React.MouseEvent<HTMLDivElement>,
                                  currActionHash,
                                );
                              }}
                              onChange={(integrationId, actionId) => {
                                handleActionChange(
                                  store,
                                  integrationId,
                                  actionId,
                                  "loop",
                                  onChangeAction,
                                  onClickAction,
                                );
                              }}
                            />
                          </div>
                        ))}
                      </Draggable>
                    </div>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </StyledButtonContainer>

        {/* Nested Advanced Logic (Task 7b): the root loop may itself hold a
            single nested Conditional Logic block -- mirrors NestedLoopGroup's
            own layout order (actions -> block card -> Add Action -> fan). */}
        {advanced?.config.oneofKind === "branches" && (
          <BranchesBlockCard
            instanceId={advanced.instanceId}
            isActive={
              activeItem?.type === "branches" &&
              activeItem.instanceId === advanced.instanceId
            }
            onSelect={onSelectNestedBlock}
            onDeleted={setActiveSection}
          />
        )}

        <SmallAddActionButton
          disabled={
            candidate.totalStepCount >=
              store.featureFlags.limits.maxActionsPerAutomation ||
            !candidate.self.trigger.triggerReference?.triggerId
          }
          advancedLogic={{
            looping: false,
            branches: candidate.branches.canAddBlockInLoop(rootLoopId),
          }}
          onClick={(integrationId: string, actionId: string): void => {
            if (integrationId === "branches") {
              const id = candidate.branches.addBlockInLoop(rootLoopId);
              if (id !== undefined) onSelectNestedBlock(id);
              return;
            }

            const hash = uuid();

            candidate.looping.setActions(
              A.mergeEmptyAction(hash, integrationId, actionId),
            );

            onClickAction(hash);
          }}
        />

        {advanced?.config.oneofKind === "branches" && (
          <NestedBranchesFan
            instanceId={advanced.instanceId}
            activeItem={activeItem}
            activeAction={activeAction}
            onSelect={onSelectBranch}
            onClickAction={onClickAction}
            onClickTrigger={onClickTrigger}
            onChangeAction={onChangeAction}
            onSelectNestedBlock={onSelectNestedBlock}
            onSelectNestedLoop={onSelectNestedLoop}
          />
        )}
      </Container>
    );
  },
);
